import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

export const useClientesFavoritos = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [favoritos, setFavoritos] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  const cargar = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('cliente_favoritos')
      .select('cliente_id')
      .eq('user_id', user.id);
    if (!error && data) {
      setFavoritos(new Set(data.map((r) => r.cliente_id)));
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const toggle = useCallback(
    async (clienteId: string) => {
      if (!user) return;
      const esFavorito = favoritos.has(clienteId);
      // Optimistic update
      setFavoritos((prev) => {
        const next = new Set(prev);
        if (esFavorito) next.delete(clienteId);
        else next.add(clienteId);
        return next;
      });
      const { error } = esFavorito
        ? await supabase
            .from('cliente_favoritos')
            .delete()
            .eq('user_id', user.id)
            .eq('cliente_id', clienteId)
        : await supabase
            .from('cliente_favoritos')
            .insert({ user_id: user.id, cliente_id: clienteId });
      if (error) {
        // Revert on error
        setFavoritos((prev) => {
          const next = new Set(prev);
          if (esFavorito) next.add(clienteId);
          else next.delete(clienteId);
          return next;
        });
        toast({
          title: 'Error',
          description: 'No se pudo actualizar el favorito.',
          variant: 'destructive',
        });
      }
    },
    [favoritos, user, toast]
  );

  const esFavorito = useCallback(
    (clienteId: string) => favoritos.has(clienteId),
    [favoritos]
  );

  return { favoritos, esFavorito, toggle, loading, recargar: cargar };
};
